import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Film, Download, Trash2, Play, X, RefreshCw, HardDrive, Search, Truck, Calendar } from 'lucide-react';
import api from '../services/api';

const fmtBytes = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtDate = (mtime) => new Date(mtime * 1000).toLocaleString();

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [facets, setFacets] = useState({ devices: [], dates: [] });
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);   // {name}
  const [q, setQ] = useState('');
  const [fDevice, setFDevice] = useState('');
  const [fDate, setFDate] = useState('');
  const token = localStorage.getItem('token') || '';

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/videos')
      .then(r => { setVideos(r.data.videos || []); setFacets(r.data.facets || { devices: [], dates: [] }); })
      .catch(() => { setVideos([]); setFacets({ devices: [], dates: [] }); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const srcFor = (name) =>
    `/api/videos/file?name=${encodeURIComponent(name)}&token=${encodeURIComponent(token)}`;
  const thumbFor = (name) =>
    `/api/videos/thumb?name=${encodeURIComponent(name)}&token=${encodeURIComponent(token)}`;

  const save = (name) => {
    const a = document.createElement('a');
    a.href = srcFor(name) + '&dl=1'; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const del = (name) => {
    if (!window.confirm(`¿Borrar "${name}"? No se puede deshacer.`)) return;
    api.delete(`/api/videos?name=${encodeURIComponent(name)}`).then(load).catch(() => {});
  };

  const shown = useMemo(() => videos.filter(v => {
    if (fDevice && v.device_id !== fDevice) return false;
    if (fDate && (v.recorded_at || '').slice(0, 10) !== fDate) return false;
    if (q) {
      const hay = `${v.name} ${v.device_name || ''} ${v.recorded_at || ''}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [videos, fDevice, fDate, q]);

  const total = shown.reduce((a, v) => a + (v.size || 0), 0);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.title}><div style={s.titleIcon}><Film size={20} /></div>
          <div><div style={s.h1}>Videos descargados</div>
            <div style={s.h1sub}>Galería de las grabaciones que bajaste al servidor</div></div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={s.pill}><HardDrive size={13} /> {shown.length} · {fmtBytes(total)}</span>
          <button style={s.reload} onClick={load}><RefreshCw size={15} /> Actualizar</button>
        </div>
      </div>

      {/* Barra de filtros */}
      <div style={s.filters}>
        <div style={s.searchBox}>
          <Search size={15} color="#94a3b8" />
          <input style={s.search} value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Buscar por nombre, unidad o fecha…" />
        </div>
        <div style={s.chips}>
          <Chip active={!fDevice} onClick={() => setFDevice('')} icon={<Truck size={13} />} label="Todas las unidades" />
          {facets.devices.map(d => (
            <Chip key={d.device_id} active={fDevice === d.device_id}
                  onClick={() => setFDevice(fDevice === d.device_id ? '' : d.device_id)}
                  label={d.device_name || d.device_id} />
          ))}
        </div>
        {facets.dates.length > 0 && (
          <div style={s.chips}>
            <Chip active={!fDate} onClick={() => setFDate('')} icon={<Calendar size={13} />} label="Todas las fechas" />
            {facets.dates.slice(0, 8).map(d => (
              <Chip key={d} active={fDate === d} onClick={() => setFDate(fDate === d ? '' : d)} label={d} />
            ))}
          </div>
        )}
      </div>

      {loading ? <div style={s.muted}>Cargando…</div> : (
        shown.length === 0 ? (
          <div style={s.empty}>
            <Film size={34} color="#cbd5e1" />
            <div style={{ marginTop: 8 }}>
              {videos.length === 0 ? 'Aún no hay videos descargados.' : 'Ningún video coincide con el filtro.'}
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
              Baja grabaciones desde <b>Grabaciones</b> y aquí aparecerán para ver, guardar o borrar.
            </div>
          </div>
        ) : (
          <div style={s.grid}>
            {shown.map(v => (
              <VideoCard key={v.name} v={v} thumb={thumbFor(v.name)}
                         onPlay={() => setPlayer({ name: v.name })}
                         onSave={() => save(v.name)} onDelete={() => del(v.name)} />
            ))}
          </div>
        )
      )}

      {player && (
        <div style={s.modal} onClick={() => setPlayer(null)}>
          <div style={s.modalInner} onClick={e => e.stopPropagation()}>
            <div style={s.modalHead}>
              <span>{player.name}</span>
              <button style={s.closeBtn} onClick={() => setPlayer(null)}><X size={18} /></button>
            </div>
            <div style={s.modalVideo}>
              <video src={srcFor(player.name)} controls autoPlay
                     style={{ width: '100%', height: '100%', background: '#000' }} />
            </div>
            <div style={s.modalFoot}>
              <button style={s.saveBtn} onClick={() => save(player.name)}><Download size={14} /> Guardar</button>
              <button style={s.delBtn2} onClick={() => { del(player.name); setPlayer(null); }}><Trash2 size={14} /> Borrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Chip = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} style={{ ...s.chip, ...(active ? s.chipActive : {}) }}>
    {icon}{label}
  </button>
);

const VideoCard = ({ v, thumb, onPlay, onSave, onDelete }) => {
  const [hover, setHover] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  return (
    <div style={{ ...s.card, ...(hover ? s.cardHover : {}) }}
         onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div style={s.thumb} onClick={onPlay} title="Reproducir">
        {imgOk
          ? <img src={thumb} alt="" style={s.thumbImg} onError={() => setImgOk(false)} />
          : <div style={s.thumbFallback} />}
        <div style={{ ...s.playOverlay, opacity: hover ? 1 : 0.85 }}>
          <div style={s.playCircle}><Play size={22} color="#fff" fill="#fff" /></div>
        </div>
        <div style={s.badges}>
          {v.channel != null && <span style={s.badge}>Cam {v.channel}</span>}
          {!v.is_mp4 && <span style={{ ...s.badge, background: 'rgba(245,158,11,.92)' }}>convierte al ver</span>}
        </div>
      </div>
      <div style={s.cardBody}>
        <div style={s.name} title={v.name}>{v.device_name || v.name}</div>
        <div style={s.meta}>
          {v.recorded_at ? v.recorded_at : fmtDate(v.mtime)} · {fmtBytes(v.size)}
        </div>
        <div style={s.actions}>
          <button style={s.playBtn} onClick={onPlay}><Play size={14} /> Ver</button>
          <button style={s.saveBtn} onClick={onSave}><Download size={14} /> Guardar</button>
          <button style={s.delBtn} onClick={onDelete} title="Borrar"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
};

const RING = '1px solid #e5e7eb';
const s = {
  page: { padding: 22, maxWidth: 1240, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  title: { display: 'flex', alignItems: 'center', gap: 12 },
  titleIcon: { width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' },
  h1: { fontSize: 19, fontWeight: 700, color: '#0f172a' },
  h1sub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#eef2f7', color: '#475569' },
  reload: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 9, border: RING, background: '#fff', cursor: 'pointer', color: '#334155', fontWeight: 600, fontSize: 13 },
  filters: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 },
  searchBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: RING, borderRadius: 10, padding: '9px 12px', maxWidth: 460 },
  search: { border: 'none', outline: 'none', fontSize: 14, flex: 1, color: '#0f172a', background: 'transparent' },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, border: RING, background: '#fff', color: '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  chipActive: { background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' },
  muted: { color: '#94a3b8', fontSize: 14, padding: 24, textAlign: 'center' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#475569', background: '#fff', border: RING, borderRadius: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 },
  card: { background: '#fff', border: RING, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,.05)', transition: 'transform .15s ease, box-shadow .15s ease' },
  cardHover: { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(15,23,42,.14)' },
  thumb: { position: 'relative', height: 148, background: '#0f172a', cursor: 'pointer', overflow: 'hidden' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbFallback: { width: '100%', height: '100%', background: 'linear-gradient(135deg,#1e293b,#0f172a)' },
  playOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity .15s ease', background: 'linear-gradient(180deg,rgba(0,0,0,.05),rgba(0,0,0,.35))' },
  playCircle: { width: 52, height: 52, borderRadius: '50%', background: 'rgba(37,99,235,.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(0,0,0,.35)' },
  badges: { position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6 },
  badge: { fontSize: 11, fontWeight: 700, color: '#fff', background: 'rgba(15,23,42,.78)', padding: '3px 8px', borderRadius: 6, backdropFilter: 'blur(2px)' },
  cardBody: { padding: 12 },
  name: { fontWeight: 700, color: '#0f172a', fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { fontSize: 12, color: '#64748b', marginTop: 3 },
  actions: { display: 'flex', gap: 6, marginTop: 10 },
  playBtn: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', border: 'none', borderRadius: 8, background: '#eff6ff', color: '#2563eb', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  saveBtn: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', border: RING, borderRadius: 8, background: '#fff', color: '#3b82f6', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  delBtn: { border: '1px solid #fecaca', background: '#fff', borderRadius: 8, width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dc2626' },
  delBtn2: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: '1px solid #fecaca', background: '#fff', borderRadius: 8, color: '#dc2626', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(2,6,23,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modalInner: { width: '82vw', maxWidth: 1000, background: '#0f172a', borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,.5)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', color: '#e5e7eb', fontSize: 14, fontWeight: 600, fontFamily: 'monospace' },
  modalVideo: { aspectRatio: '16 / 9', background: '#000' },
  modalFoot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, background: '#0b1120' },
  closeBtn: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex' },
};

export default Videos;
